import { getVm } from '@/helpers/vm-helper';
import { UpcomingReleaseLabel } from '@/@types/graphql-types';

interface ReleaseRange {
	type: string;
	date: string;
}

enum ReleaseRangeType {
	DAY = 'day',
	MONTH = 'month',
	SEASON = 'season',
	YEAR = 'year',
}

/**
 * shift a given date by a number of days forwards or backwards and make sure the given date is of a certain timezone.
 * @param timeZone name of the timezone, e.g. 'Europe/Berlin', usually store.getters['language/localeObject].timezone is taken
 * @param options
 * @param shiftDays positive or negative number of days to shift
 * @param targetDate
 * @returns
 */
export const shiftDateWithTimezone = (timeZone: string, shiftDays = 0, targetDate = new Date()) => {
	// we can hard code en-US because in the end we're only interested in the Date object
	// if time shall not be reset, then use targetDate.toLocaleString.
	const localisedDateString = targetDate.toLocaleDateString('en-US', { timeZone, timeZoneName: 'short' });
	// check if any days need to be shifted
	if (shiftDays !== 0) {
		const shiftedDate = new Date(localisedDateString);
		shiftedDate.setDate(shiftedDate.getDate() + shiftDays);
		return shiftedDate;
	}
	return new Date(localisedDateString);
};

export function shiftHours(_date: Date | string, shift: number): Date;
export function shiftHours(_date: undefined, shift: number): undefined;
export function shiftHours(_date: Date | string | undefined, shift: number) {
	if (_date == null) return undefined;

	const date = new Date(_date);
	date.setTime(date.getTime() + shift * 60 * 60 * 1000);
	return date;
}

export function getWeekNumber(d: Date) {
	const target = new Date(d.valueOf());
	const dayNr = (d.getDay() + 6) % 7;
	target.setDate(target.getDate() - dayNr + 3);
	const firstThursday = target.valueOf();
	target.setMonth(0, 1);

	if (target.getDay() !== 4) {
		target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
	}

	return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
}

export function isSameDate(left: Date, right: Date): boolean {
	return left.toDateString() === right.toDateString();
}

export function isToday(targetDate: Date) {
	return isSameDate(targetDate, new Date());
}

export function isSameWeek(left: Date, right: Date) {
	return getWeekNumber(left) === getWeekNumber(right);
}

export function getShortMonthName(targetDate: Date, locale: string) {
	return new Intl.DateTimeFormat(locale, { month: 'short' }).format(targetDate);
}

export function isYesterday(targetDate: Date) {
	const now = new Date();
	targetDate.setHours(0);
	targetDate.setMinutes(0);
	const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
	return isSameDate(yesterday, targetDate);
}

export function isTomorrow(targetDate: Date) {
	const now = new Date();
	targetDate.setHours(0);
	targetDate.setMinutes(0);
	const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
	return isSameDate(tomorrow, targetDate);
}

export function getDateString(date = new Date()) {
	return [date.getFullYear(), ('00' + (date.getMonth() + 1)).slice(-2), ('00' + date.getDate()).slice(-2)].join('-');
}

/**
 * Format a date range into a translated human redable date.
 * This only works if `end === start` aka there is an exact expected release date.
 *
 * @param start Start point of the estimated release date range
 * @param end End point of the estimated release date range
 * @param language Current language of the client
 * @returns The end date in human readable format if it is in the future, `null` otherwise as well as date type.
 */
export function formatReleaseRange(start: Date, end: Date, language: string): ReleaseRange | null {
	const vm = getVm();

	// a little hacky but it seems to only rely on the language part to format the date
	const locale = `${language}-${language.toUpperCase()}`;
	const timeLeft = calcTimeLeft(end, start);

	if (calcTimeLeft(end).days < 0) return null;

	// Year
	if (timeLeft.weeks === 52) {
		return {
			type: ReleaseRangeType.YEAR,
			date: `${vm.$t('WEBAPP_DATE_UNKNOWN_TBA')} ${new Intl.DateTimeFormat([locale, 'en-US'], {
				year: 'numeric',
			}).format(end)}`,
		};
	}

	// Season
	if (timeLeft.months >= 2 && timeLeft.months <= 3) {
		let date;
		switch (end.getMonth()) {
			case 1:
				date = vm.$t('WEBAPP_WINTER');
				break;
			case 4:
				date = vm.$t('WEBAPP_SPRING');
				break;
			case 7:
				date = vm.$t('WEBAPP_SUMMER');
				break;
			case 10:
				date = vm.$t('WEBAPP_FALL');
				break;
			default:
				date = vm.$t('WEBAPP_DATE_UNKNOWN_TBA');
				break;
		}
		return { type: ReleaseRangeType.SEASON, date };
	}

	// Month
	if (timeLeft.weeks > 3 && timeLeft.weeks <= 5) {
		return {
			type: ReleaseRangeType.MONTH,
			date: new Intl.DateTimeFormat([locale, 'en-US'], { month: 'long' }).format(end),
		};
	}

	// Day
	// If the release range is greater than one day it means no
	// release date has actually been announced/found yet
	if (Object.values(timeLeft).every(time => time === 0)) {
		return {
			type: ReleaseRangeType.DAY,
			date: new Intl.DateTimeFormat([locale, 'en-US'], { month: 'long', day: 'numeric', year: 'numeric' }).format(
				end
			),
		};
	}

	// Fallback if a date is present but doesn't fit a defined range above
	if (timeLeft) {
		return {
			type: ReleaseRangeType.DAY,
			date: vm.$t('WEBAPP_DATE_UNKNOWN_TBA'),
		};
	}

	return null;
}

function getWeeksLeftLabel(weeksLeft: number) {
	const vm = getVm();
	return weeksLeft === 1
		? vm.$t('WEBAPP_WEEK_LEFT', { weeks: weeksLeft })
		: vm.$t('WEBAPP_WEEKS_LEFT', { weeks: weeksLeft });
}

export function formattedReleaseDate(
	date: string,
	label: UpcomingReleaseLabel,
	language: string,
	dateFormat: 'short' | 'long' = 'short'
): { heading: string; subheading: string | null; exactDateAvailable: boolean } {
	const vm = getVm();
	const dateObject = new Date(date);
	let heading = null;
	let subheading = null;
	let exactDateAvailable = false;

	switch (label) {
		case UpcomingReleaseLabel.Date:
			const weeksLeft = diffInWeeks(dateObject);
			heading = new Intl.DateTimeFormat([language], { dateStyle: dateFormat }).format(dateObject);
			subheading = weeksLeft > 0 ? ' ' + getWeeksLeftLabel(weeksLeft) : '';
			exactDateAvailable = true;
			break;
		case UpcomingReleaseLabel.Month:
			heading = new Intl.DateTimeFormat([language], { month: 'long' }).format(dateObject);
			break;
		case UpcomingReleaseLabel.Year:
			heading = new Intl.DateTimeFormat([language], {
				year: 'numeric',
			}).format(dateObject);
			break;
		case UpcomingReleaseLabel.Today:
			heading = vm.$t('WEBAPP_TODAY');
			break;
		case UpcomingReleaseLabel.Tomorrow:
			heading = vm.$t('WEBAPP_DATE_TOMORROW');
			break;
		case UpcomingReleaseLabel.Autumn:
			heading = vm.$t('WEBAPP_FALL');
			break;
		case UpcomingReleaseLabel.Winter:
			heading = vm.$t('WEBAPP_WINTER');
			break;
		case UpcomingReleaseLabel.Spring:
			heading = vm.$t('WEBAPP_SPRING');
			break;
		case UpcomingReleaseLabel.Summer:
			heading = vm.$t('WEBAPP_SUMMER');
			break;
		default:
			heading = vm.$t('WEBAPP_DATE_UNKNOWN_TBA');
	}

	return {
		heading,
		subheading,
		exactDateAvailable,
	};
}

export function releaseDateCounter(releaseDate: Date) {
	const vm = getVm();
	const { days, weeks, months } = calcTimeLeft(releaseDate);
	const daysLeft = Math.max(0, releaseDate.getDate() - new Date().getDate());

	if (releaseDate.getDate() - new Date().getDate() === 1 && daysLeft < 2 && months === 0) {
		return vm.$t('WEBAPP_DATE_TOMORROW');
	}

	if (days <= 0) {
		return vm.$t('WEBAPP_TODAY');
	}

	if (days < 14) {
		return vm.$t('WEBAPP_DAYS_LEFT', { daysLeft: days }) || `${days} days left`;
	}

	return getWeeksLeftLabel(weeks);
}

/**
 * Calculate the amount of time between an end date and an optional start date (end - start).
 * If start is not passed, it defaults to "now", i.e. `new Date()`.
 *
 * @param end Date from which to calculate the time left
 * @param start Starting time of the calculation defaults to now (new Date())
 */
export function calcTimeLeft(end: Date, start: Date = new Date()) {
	const yearsLeft = end.getFullYear() - start.getFullYear();

	return {
		days: diffInDays(end, start),
		weeks: diffInWeeks(end, start),
		months: end.getMonth() - start.getMonth() + 12 * yearsLeft,
		years: yearsLeft,
	};
}

export function diffInWeeks(end: Date, start: Date = new Date()) {
	const msPerHour = 60 * 60 * 1000;
	const msLeft = diffInMilliseconds(end, start);

	return Math.floor(msLeft / (msPerHour * 24 * 7));
}

export function diffInDays(end: Date, start: Date = new Date()) {
	const msPerHour = 60 * 60 * 1000;
	const msLeft = diffInMilliseconds(end, start);

	return Math.floor(msLeft / (msPerHour * 24));
}

export function diffInMilliseconds(end: Date, start: Date = new Date()) {
	return end.getTime() - start.getTime();
}
